<script setup lang="ts">
import type { CheckboxGroupProps } from '@formwerk/core'
import { useCheckboxGroup, useFormContext } from '@formwerk/core'

interface CheckboxGroupFieldProps extends CheckboxGroupProps<string> {
  /**
   * Visually hides the label while keeping it accessible to screen readers.
   */
  hideLabel?: boolean
  wrap?: boolean
}

const props = withDefaults(defineProps<CheckboxGroupFieldProps>(), {
  hideLabel: false,
  wrap: true,
})

const { groupProps, labelProps, errorMessage, errorMessageProps, isBlurred }
  = useCheckboxGroup(props)

// Inside a form, errors only show after a submit attempt, then update live as the user fixes them
const form = useFormContext()
const errored = computed(() => (form ? form.isSubmitAttempted.value : isBlurred.value) && !!errorMessage.value)
</script>

<template>
  <div>
    <span
      v-if="label"
      v-bind="labelProps"
      class="typo-sans-1 mb-2 block uppercase"
      :class="{ 'sr-only': hideLabel }"
    >
      {{ label }}
    </span>

    <div
      v-bind="groupProps"
      class="flex gap-1"
      :class="[{ 'c-red': errored }, wrap ? 'flex-wrap' : 'flex-nowrap']"
    >
      <slot />
    </div>

    <TransitionDefault>
      <p
        v-if="errored"
        v-bind="errorMessageProps"
        class="text-red-500 typo-sans-1 mt-1"
      >
        {{ errorMessage }}
      </p>
    </TransitionDefault>
  </div>
</template>
